import React, { useState, useRef, useCallback, useEffect } from "react";
import {
  Type, AlignLeft, Mail, Phone, Hash, Lock, Link2, ChevronDown, ChevronRight,
  CheckSquare, Circle, Calendar, Upload, Minus, Heading, AlignCenter,
  GripVertical, Trash2, Copy, Eye, Save, Plus, X, Settings, ChevronUp,
  LayoutTemplate, Columns2,
  Maximize2, AlertCircle, CheckCircle2,
} from "lucide-react";
import "../../assets/css/FormBuilder.css";

/* ── Category colours ─────────────────────────────────── */
const CAT_STYLE = {
  Basic:         { color: "#3b82f6", bg: "#eff6ff", border: "#bfdbfe" },
  Choice:        { color: "#8b5cf6", bg: "#f5f3ff", border: "#ddd6fe" },
  "Date & File": { color: "#0891b2", bg: "#ecfeff", border: "#a5f3fc" },
  Layout:        { color: "#059669", bg: "#ecfdf5", border: "#a7f3d0" },
};
const TYPE_CAT = {
  text:"Basic",textarea:"Basic",email:"Basic",phone:"Basic",number:"Basic",password:"Basic",url:"Basic",
  select:"Choice",checkbox:"Choice",radio:"Choice",
  date:"Date & File",file:"Date & File",
  heading:"Layout",paragraph:"Layout",divider:"Layout",
};
const typeStyle = (t) => CAT_STYLE[TYPE_CAT[t]] || { color:"#64748b", bg:"#f1f5f9", border:"#e2e8f0" };

/* ── Field groups ─────────────────────────────────────── */
const FIELD_GROUPS = [
  { label:"Basic",        fields:[
    { type:"text",      label:"Short Text",  icon:Type },
    { type:"textarea",  label:"Long Text",   icon:AlignLeft },
    { type:"email",     label:"Email",       icon:Mail },
    { type:"phone",     label:"Phone",       icon:Phone },
    { type:"number",    label:"Number",      icon:Hash },
    { type:"password",  label:"Password",    icon:Lock },
    { type:"url",       label:"Website URL", icon:Link2 },
  ]},
  { label:"Choice",       fields:[
    { type:"select",    label:"Dropdown",    icon:ChevronDown },
    { type:"checkbox",  label:"Checkboxes",  icon:CheckSquare },
    { type:"radio",     label:"Radio Group", icon:Circle },
  ]},
  { label:"Date & File",  fields:[
    { type:"date",      label:"Date",        icon:Calendar },
    { type:"file",      label:"File Upload", icon:Upload },
  ]},
  { label:"Layout",       fields:[
    { type:"heading",   label:"Heading",     icon:Heading },
    { type:"paragraph", label:"Paragraph",   icon:AlignCenter },
    { type:"divider",   label:"Divider",     icon:Minus },
  ]},
];

const CHOICE_TYPES = ["select","checkbox","radio"];
const LAYOUT_TYPES = ["heading","paragraph","divider"];
let _uid = 1;
const uid = () => `f_${_uid++}`;

const makeField = (type) => {
  const base = { id:uid(), type, label:"", required:false, placeholder:"", helpText:"", width:"full" };
  const m = {
    text:      { ...base, label:"Short Text",    minLength:"", maxLength:"" },
    textarea:  { ...base, label:"Long Text",     minLength:"", maxLength:"" },
    email:     { ...base, label:"Email Address", placeholder:"you@example.com" },
    phone:     { ...base, label:"Phone Number",  placeholder:"+1 (555) 000-0000" },
    number:    { ...base, label:"Number",        min:"", max:"", step:"" },
    password:  { ...base, label:"Password",      placeholder:"••••••••", minLength:"" },
    url:       { ...base, label:"Website URL",   placeholder:"https://" },
    date:      { ...base, label:"Date",          minDate:"", maxDate:"" },
    file:      { ...base, label:"File Upload",   accept:"", multiple:false },
    select:    { ...base, label:"Dropdown",      options:["Option 1","Option 2","Option 3"] },
    checkbox:  { ...base, label:"Checkboxes",    options:["Option 1","Option 2"] },
    radio:     { ...base, label:"Radio Group",   options:["Option 1","Option 2"] },
    heading:   { id:uid(), type, text:"Section Heading", level:"h2", width:"full" },
    paragraph: { id:uid(), type, text:"Add a description here.", width:"full" },
    divider:   { id:uid(), type, width:"full" },
  };
  return m[type] || base;
};

/* ── Collapsible props section ────────────────────────── */
const PropSection = ({ title, children, defaultOpen = true }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="fb-prop-section">
      <button className="fb-prop-section-hd" onClick={() => setOpen(o => !o)}>
        <span>{title}</span>
        {open ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
      </button>
      {open && <div className="fb-prop-section-body">{children}</div>}
    </div>
  );
};

/* ── Canvas card ──────────────────────────────────────── */
const CanvasCard = ({ field, index, total, isSelected, onSelect, onDelete, onDuplicate, onDragStart, onDragEnd }) => {
  const isLayout = LAYOUT_TYPES.includes(field.type);

  return (
    <div
      className={`fb-canvas-card${isSelected ? " selected" : ""}`}
      draggable
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
      onClick={onSelect}
    >
      <div className="fb-card-drag"><GripVertical size={16} /></div>

      <div className="fb-card-body">
        {field.type === "divider"   && <div className="fb-canvas-divider-line" />}
        {field.type === "heading"   && <div className={`fb-canvas-heading fb-canvas-${field.level || "h2"}`}>{field.text || "Heading"}</div>}
        {field.type === "paragraph" && <p className="fb-canvas-para">{field.text || "Paragraph text…"}</p>}

        {!isLayout && (
          <>
            <div className="fb-card-label-row">
              <span className="fb-card-label">{field.label || "Untitled"}</span>
              {field.required && <span className="fb-card-req">Required</span>}
              {field.width === "half" && <span className="fb-card-width-badge"><Columns2 size={10} />1/2</span>}
            </div>
            {field.type === "textarea" && <div className="fb-card-mock fb-card-mock-tall" />}
            {field.type === "select"   && <div className="fb-card-mock fb-card-mock-select"><span className="fb-mock-placeholder">{(field.options||[])[0]||"Select…"}</span><ChevronDown size={13} color="#94a3b8" /></div>}
            {field.type === "file"     && <div className="fb-card-mock fb-card-mock-file"><Upload size={14} /><span>Click to upload</span></div>}
            {field.type === "checkbox" && <div className="fb-card-choices">{(field.options||[]).slice(0,3).map((o,i)=><span key={i} className="fb-card-choice"><span className="fb-check-box"/>{o}</span>)}</div>}
            {field.type === "radio"    && <div className="fb-card-choices">{(field.options||[]).slice(0,3).map((o,i)=><span key={i} className="fb-card-choice"><span className="fb-radio-dot"/>{o}</span>)}</div>}
            {!["textarea","select","file","checkbox","radio"].includes(field.type) && (
              <div className="fb-card-mock"><span className="fb-mock-placeholder">{field.placeholder || "Your answer…"}</span></div>
            )}
          </>
        )}
      </div>

      <div className="fb-card-actions" onClick={e => e.stopPropagation()}>
        <button className="fb-act-btn" onClick={onDuplicate} title="Duplicate"><Copy   size={13}/></button>
        <button className="fb-act-btn fb-act-del" onClick={onDelete} title="Delete"><Trash2 size={13}/></button>
      </div>
    </div>
  );
};

/* ── Properties panel ─────────────────────────────────── */
const PropertiesPanel = ({ field, onChange }) => {
  if (!field) return (
    <div className="fb-props-empty">
      <div className="fb-props-empty-icon"><Settings size={26} strokeWidth={1.4} /></div>
      <p className="fb-props-empty-title">No field selected</p>
      <p className="fb-props-empty-sub">Click a field on the canvas to edit its properties</p>
    </div>
  );

  const isLayout = LAYOUT_TYPES.includes(field.type);
  const isChoice = CHOICE_TYPES.includes(field.type);
  const cs       = typeStyle(field.type);

  const upd = (patch) => onChange(patch);

  const updateOption = (i, val) => {
    const opts = [...(field.options || [])]; opts[i] = val; upd({ options: opts });
  };
  const addOption    = () => upd({ options:[...(field.options||[]), `Option ${(field.options||[]).length+1}`] });
  const removeOption = (i) => upd({ options:(field.options||[]).filter((_,idx)=>idx!==i) });

  return (
    <div className="fb-props">
      {/* Header */}
      <div className="fb-props-head" style={{ "--cat-color": cs.color, "--cat-bg": cs.bg }}>
        <div className="fb-props-head-icon" style={{ background: cs.bg, border:`1px solid ${cs.border||"#e2e8f0"}` }}>
          {React.createElement(
            FIELD_GROUPS.flatMap(g=>g.fields).find(f=>f.type===field.type)?.icon || Type,
            { size:14, color:cs.color }
          )}
        </div>
        <div>
          <p className="fb-props-head-title">Properties</p>
          <span className="fb-props-type-badge" style={{ color:cs.color, background:cs.bg }}>{field.type}</span>
        </div>
      </div>

      {/* Divider only */}
      {field.type === "divider" && (
        <p className="fb-props-hint"><AlertCircle size={13} /> Divider has no editable properties.</p>
      )}

      {/* Heading */}
      {field.type === "heading" && (
        <PropSection title="Content">
          <div className="fb-pg"><label className="fb-pl">Text</label>
            <input className="fb-pi" value={field.text||""} onChange={e=>upd({text:e.target.value})} placeholder="Heading text"/>
          </div>
          <div className="fb-pg"><label className="fb-pl">Level</label>
            <select className="fb-pi" value={field.level||"h2"} onChange={e=>upd({level:e.target.value})}>
              <option value="h1">H1 — Large</option>
              <option value="h2">H2 — Medium</option>
              <option value="h3">H3 — Small</option>
            </select>
          </div>
        </PropSection>
      )}

      {/* Paragraph */}
      {field.type === "paragraph" && (
        <PropSection title="Content">
          <div className="fb-pg"><label className="fb-pl">Text</label>
            <textarea className="fb-pi fb-pi-ta" rows={3} value={field.text||""} onChange={e=>upd({text:e.target.value})} placeholder="Paragraph text"/>
          </div>
        </PropSection>
      )}

      {/* All input fields */}
      {!isLayout && (
        <>
          <PropSection title="Basic">
            <div className="fb-pg"><label className="fb-pl">Label</label>
              <input className="fb-pi" value={field.label||""} onChange={e=>upd({label:e.target.value})} placeholder="Field label"/>
            </div>
            {!isChoice && field.type !== "file" && (
              <div className="fb-pg"><label className="fb-pl">Placeholder</label>
                <input className="fb-pi" value={field.placeholder||""} onChange={e=>upd({placeholder:e.target.value})} placeholder="Placeholder…"/>
              </div>
            )}
            <div className="fb-pg"><label className="fb-pl">Help Text</label>
              <input className="fb-pi" value={field.helpText||""} onChange={e=>upd({helpText:e.target.value})} placeholder="Optional hint below field"/>
            </div>

            {/* Required toggle */}
            <div className="fb-prop-row">
              <div>
                <p className="fb-pl mb-0">Required</p>
                <p className="fb-psub">Must be filled before submit</p>
              </div>
              <button className={`fb-toggle${field.required?" on":""}`} onClick={()=>upd({required:!field.required})}>
                <span className="fb-toggle-thumb"/>
              </button>
            </div>
          </PropSection>

          {/* Choice options */}
          {isChoice && (
            <PropSection title="Options">
              <div className="fb-options-list">
                {(field.options||[]).map((opt,i)=>(
                  <div key={i} className="fb-option-row">
                    <span className="fb-opt-bullet" style={{background:cs.color}}/>
                    <input className="fb-pi" value={opt} onChange={e=>updateOption(i,e.target.value)} placeholder={`Option ${i+1}`}/>
                    <button className="fb-opt-del" onClick={()=>removeOption(i)} disabled={(field.options||[]).length<=1}><X size={12}/></button>
                  </div>
                ))}
              </div>
              <button className="fb-add-opt-btn" style={{"--cat-color":cs.color}} onClick={addOption}>
                <Plus size={12}/> Add Option
              </button>
            </PropSection>
          )}

          {/* File settings */}
          {field.type === "file" && (
            <PropSection title="File Settings">
              <div className="fb-pg"><label className="fb-pl">Accepted Types</label>
                <input className="fb-pi" value={field.accept||""} onChange={e=>upd({accept:e.target.value})} placeholder="e.g. .pdf, .jpg, image/*"/>
              </div>
              <div className="fb-prop-row">
                <div>
                  <p className="fb-pl mb-0">Multiple Files</p>
                  <p className="fb-psub">Allow uploading multiple files</p>
                </div>
                <button className={`fb-toggle${field.multiple?" on":""}`} onClick={()=>upd({multiple:!field.multiple})}>
                  <span className="fb-toggle-thumb"/>
                </button>
              </div>
            </PropSection>
          )}

          {/* Validation */}
          {["text","textarea","password"].includes(field.type) && (
            <PropSection title="Validation" defaultOpen={false}>
              <div className="fb-pg-row">
                <div className="fb-pg fb-pg-half">
                  <label className="fb-pl">Min Length</label>
                  <input className="fb-pi" type="number" min="0" value={field.minLength||""} onChange={e=>upd({minLength:e.target.value})} placeholder="0"/>
                </div>
                <div className="fb-pg fb-pg-half">
                  <label className="fb-pl">Max Length</label>
                  <input className="fb-pi" type="number" min="0" value={field.maxLength||""} onChange={e=>upd({maxLength:e.target.value})} placeholder="∞"/>
                </div>
              </div>
            </PropSection>
          )}
          {field.type === "number" && (
            <PropSection title="Validation" defaultOpen={false}>
              <div className="fb-pg-row">
                <div className="fb-pg fb-pg-half">
                  <label className="fb-pl">Min Value</label>
                  <input className="fb-pi" type="number" value={field.min||""} onChange={e=>upd({min:e.target.value})} placeholder="—"/>
                </div>
                <div className="fb-pg fb-pg-half">
                  <label className="fb-pl">Max Value</label>
                  <input className="fb-pi" type="number" value={field.max||""} onChange={e=>upd({max:e.target.value})} placeholder="—"/>
                </div>
              </div>
              <div className="fb-pg">
                <label className="fb-pl">Step</label>
                <input className="fb-pi" type="number" min="0" value={field.step||""} onChange={e=>upd({step:e.target.value})} placeholder="1"/>
              </div>
            </PropSection>
          )}
          {field.type === "date" && (
            <PropSection title="Validation" defaultOpen={false}>
              <div className="fb-pg"><label className="fb-pl">Min Date</label>
                <input className="fb-pi" type="date" value={field.minDate||""} onChange={e=>upd({minDate:e.target.value})}/>
              </div>
              <div className="fb-pg"><label className="fb-pl">Max Date</label>
                <input className="fb-pi" type="date" value={field.maxDate||""} onChange={e=>upd({maxDate:e.target.value})}/>
              </div>
            </PropSection>
          )}

          {/* Appearance */}
          <PropSection title="Appearance" defaultOpen={false}>
            <div className="fb-pg">
              <label className="fb-pl">Field Width</label>
              <div className="fb-width-btns">
                <button className={`fb-width-btn${field.width==="full"?" active":""}`} onClick={()=>upd({width:"full"})}>
                  <Maximize2 size={13}/> Full Width
                </button>
                <button className={`fb-width-btn${field.width==="half"?" active":""}`} onClick={()=>upd({width:"half"})}>
                  <Columns2 size={13}/> Half Width
                </button>
              </div>
            </div>
          </PropSection>
        </>
      )}
    </div>
  );
};

/* ── Preview field ────────────────────────────────────── */
const PreviewField = ({ field }) => {
  const isChoice = CHOICE_TYPES.includes(field.type);
  const colSpan  = (!field.width || field.width === "full") ? "1/-1" : undefined;

  if (field.type === "divider")   return <hr className="fb-prev-divider" style={{gridColumn:"1/-1"}} />;
  if (field.type === "heading")   return <p className={`fb-prev-heading fb-prev-${field.level||"h2"}`} style={{gridColumn:"1/-1"}}>{field.text}</p>;
  if (field.type === "paragraph") return <p className="fb-prev-para" style={{gridColumn:"1/-1"}}>{field.text}</p>;

  return (
    <div className="fb-prev-field" style={{gridColumn: colSpan}}>
      <label className="fb-prev-label">{field.label}{field.required && <span className="fb-prev-req"> *</span>}</label>
      {field.helpText && <p className="fb-prev-help">{field.helpText}</p>}

      {field.type === "textarea" && <textarea className="fb-prev-input fb-prev-ta" placeholder={field.placeholder||"Your answer…"} rows={3} minLength={field.minLength||undefined} maxLength={field.maxLength||undefined} />}
      {field.type === "select" && (
        <select className="fb-prev-input">
          <option value="">Select an option…</option>
          {(field.options||[]).map((o,i)=><option key={i}>{o}</option>)}
        </select>
      )}
      {field.type === "checkbox" && <div className="fb-prev-choices">{(field.options||[]).map((o,i)=><label key={i} className="fb-prev-choice"><input type="checkbox"/><span>{o}</span></label>)}</div>}
      {field.type === "radio"    && <div className="fb-prev-choices">{(field.options||[]).map((o,i)=><label key={i} className="fb-prev-choice"><input type="radio" name={field.id}/><span>{o}</span></label>)}</div>}
      {field.type === "file"     && <div className="fb-prev-file"><Upload size={18}/><span>Click to upload{field.multiple?" (multiple)":""}</span>{field.accept && <span className="fb-prev-accept">{field.accept}</span>}</div>}
      {!isChoice && !["textarea","file"].includes(field.type) && (
        <input
          className="fb-prev-input"
          type={field.type==="password"?"password":field.type==="number"?"number":field.type==="date"?"date":field.type==="email"?"email":"text"}
          placeholder={field.placeholder||"Your answer…"}
          min={field.min||undefined} max={field.max||undefined} step={field.step||undefined}
          minLength={field.minLength||undefined} maxLength={field.maxLength||undefined}
        />
      )}
    </div>
  );
};

/* ── Preview modal ────────────────────────────────────── */
const PreviewModal = ({ fields, formTitle, formDesc, submitLabel, onClose }) => (
  <div className="fb-overlay" onClick={onClose}>
    <div className="fb-modal" onClick={e=>e.stopPropagation()}>
      <div className="fb-modal-hd">
        <div className="fb-modal-hd-left">
          <Eye size={16} color="var(--primaryColor)"/>
          <span className="fb-modal-title">Form Preview</span>
        </div>
        <button className="fb-modal-x" onClick={onClose}><X size={17}/></button>
      </div>
      <div className="fb-modal-body">
        <div className="fb-prev-card">
          {formTitle && <h2 className="fb-prev-form-title">{formTitle}</h2>}
          {formDesc  && <p className="fb-prev-form-desc">{formDesc}</p>}
          {(formTitle || formDesc) && <hr className="fb-prev-sep"/>}
          {fields.length === 0 && <p className="fb-prev-empty">No fields added yet.</p>}
          <div className="fb-prev-grid">
            {fields.map(f=><PreviewField key={f.id} field={f}/>)}
          </div>
          {fields.length > 0 && <button className="fb-prev-submit">{submitLabel||"Submit"}</button>}
        </div>
      </div>
    </div>
  </div>
);

/* ── Toast ────────────────────────────────────────────── */
const Toast = ({ msg, icon }) => (
  <div className="fb-toast"><span className="fb-toast-icon">{icon}</span>{msg}</div>
);

/* ── Fields Top Bar ───────────────────────────────────── */
const FieldsTopBar = ({ onAdd, counts }) => {
  const [activeGroup, setActiveGroup] = useState(null);
  const [dropStyles, setDropStyles]   = useState({});
  const btnRefs  = useRef({});
  const dropRefs = useRef({});

  useEffect(() => {
    const handleClick = (e) => {
      const inBtn  = Object.values(btnRefs.current).some(el => el && el.contains(e.target));
      const inDrop = Object.values(dropRefs.current).some(el => el && el.contains(e.target));
      if (!inBtn && !inDrop) setActiveGroup(null);
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  const openGroup = (label) => {
    if (activeGroup === label) { setActiveGroup(null); return; }
    const el = btnRefs.current[label];
    if (el) {
      const rect = el.getBoundingClientRect();
      setDropStyles(prev => ({
        ...prev,
        [label]: { position: "fixed", top: rect.bottom + 6, left: rect.left, zIndex: 99999 },
      }));
    }
    setActiveGroup(label);
  };

  return (
    <div className="fb-topbar-fields">
      <div className="fb-topbar-groups">
        {FIELD_GROUPS.map(group => {
          const cs = CAT_STYLE[group.label] || { color:"#64748b", bg:"#f1f5f9", border:"#e2e8f0" };
          const isOpen = activeGroup === group.label;
          return (
            <div key={group.label} className="fb-topbar-group-wrap">
              <button
                ref={el => btnRefs.current[group.label] = el}
                className={`fb-topbar-group-btn${isOpen ? " active" : ""}`}
                style={{ "--cat-color": cs.color, "--cat-bg": cs.bg, "--cat-border": cs.border }}
                onClick={() => openGroup(group.label)}
              >
                <span className="fb-tg-label">{group.label}</span>
                <ChevronDown size={11} className={`fb-tg-arrow${isOpen ? " open" : ""}`} />
              </button>
              {isOpen && (
                <div
                  ref={el => dropRefs.current[group.label] = el}
                  className="fb-topbar-dropdown"
                  style={dropStyles[group.label] || {}}
                >
                  {group.fields.map(({ type, label, icon: Icon }) => (
                    <button
                      key={type}
                      className="fb-topbar-field-btn"
                      onClick={() => { onAdd(type); setActiveGroup(null); }}
                    >
                      <span className="fb-pal-icon" style={{ background: "var(--primaryTint)", border: "1px solid var(--primaryTintBorder)" }}>
                        <Icon size={13} color="var(--primaryColor)" />
                      </span>
                      <span className="fb-pal-label" style={{ color: "#1e293b" }}>{label}</span>
                      <Plus size={11} color="var(--primaryColor)" style={{ marginLeft: "auto", opacity: 0.6 }} />
                    </button>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

/* ── Main ─────────────────────────────────────────────── */
const FormBuilder = () => {
  const [fields, setFields]           = useState([]);
  const [selectedId, setSelectedId]   = useState(null);
  const [formTitle, setFormTitle]     = useState("Untitled Form");
  const [formDesc, setFormDesc]       = useState("");
  const [submitLabel, setSubmitLabel] = useState("Submit");
  const [showPreview, setShowPreview] = useState(false);
  const [showClear, setShowClear]     = useState(false);
  const [toast, setToast]             = useState(null);
  const [undoStack, setUndoStack]     = useState([]);
  const [redoStack, setRedoStack]     = useState([]);
  const [dragOverIdx, setDragOverIdx] = useState(null);
  const dragSrc = useRef(null);

  const selectedField = fields.find(f => f.id === selectedId) || null;
  const canUndo = undoStack.length > 0;
  const canRedo = redoStack.length > 0;

  const fieldCounts = fields.reduce((acc, f) => { acc[f.type] = (acc[f.type]||0)+1; return acc; }, {});

  const showToast = (msg, icon = <CheckCircle2 size={15}/>)  => {
    setToast({ msg, icon });
    setTimeout(() => setToast(null), 2200);
  };

  /* ── History helpers ── */
  const undo = useCallback(() => {
    if (!undoStack.length) return;
    setRedoStack(prev => [...prev, fields]);
    setFields(undoStack[undoStack.length - 1]);
    setUndoStack(prev => prev.slice(0, -1));
    setSelectedId(null);
  }, [undoStack, fields]);
  const redo = useCallback(() => {
    if (!redoStack.length) return;
    setUndoStack(prev => [...prev, fields]);
    setFields(redoStack[redoStack.length - 1]);
    setRedoStack(prev => prev.slice(0, -1));
    setSelectedId(null);
  }, [redoStack, fields]);

  /* ── Keyboard shortcuts ── */
  useEffect(() => {
    const onKey = (e) => {
      const tag = document.activeElement?.tagName;
      const isInput = ["INPUT","TEXTAREA","SELECT"].includes(tag);
      if ((e.ctrlKey||e.metaKey) && e.key==="z") { e.preventDefault(); undo(); }
      if ((e.ctrlKey||e.metaKey) && (e.key==="y"||(e.shiftKey&&e.key==="z"))) { e.preventDefault(); redo(); }
      if (!isInput && (e.key==="Delete"||e.key==="Backspace") && selectedId) {
        e.preventDefault(); deleteField(selectedId);
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [selectedId, undo, redo]);

  /* ── Field operations (functional updates) ── */
  const addField = (type) => {
    const f = makeField(type);
    setFields(prev => { setUndoStack(u => [...u.slice(-19), prev]); setRedoStack([]); return [...prev, f]; });
    setSelectedId(f.id);
  };
  const deleteField = (id) => {
    setFields(prev => { setUndoStack(u => [...u.slice(-19), prev]); setRedoStack([]); return prev.filter(f => f.id !== id); });
    setSelectedId(prev => prev === id ? null : prev);
  };
  const duplicateField = (id) => {
    setFields(prev => {
      const idx = prev.findIndex(f => f.id === id);
      if (idx < 0) return prev;
      const copy = { ...prev[idx], id: uid() };
      setUndoStack(u => [...u.slice(-19), prev]); setRedoStack([]); setSelectedId(copy.id);
      const next = [...prev]; next.splice(idx + 1, 0, copy); return next;
    });
    showToast("Field duplicated");
  };
  const moveField = (id, dir) => {
    const idx = fields.findIndex(f => f.id === id);
    const newIdx = idx + dir;
    if (newIdx < 0 || newIdx >= fields.length) return;
    const next = [...fields];
    [next[idx], next[newIdx]] = [next[newIdx], next[idx]];
    setFields(prev => { setUndoStack(u => [...u.slice(-19), prev]); setRedoStack([]); return next; });
  };
  const updateField = useCallback((id, patch) => {
    setFields(prev => prev.map(f => f.id === id ? { ...f, ...patch } : f));
  }, []);
  const clearAll = () => {
    setFields(prev => { setUndoStack(u => [...u.slice(-19), prev]); setRedoStack([]); return []; });
    setSelectedId(null); setShowClear(false); showToast("All fields cleared");
  };

  /* ── Export JSON ── */
  const exportJSON = () => {
    const data = { title: formTitle, description: formDesc, submitLabel, fields };
    const blob = new Blob([JSON.stringify(data, null, 2)], { type:"application/json" });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement("a");
    a.href = url; a.download = `${formTitle.replace(/\s+/g,"_")}.json`; a.click();
    URL.revokeObjectURL(url);
    showToast("Form exported as JSON");
  };

  /* ── Drag reorder ── */
  const onDragStart = (e, idx) => {
    dragSrc.current = idx;
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/plain", String(idx));
  };
  const onDrop      = (e, idx) => {
    e.preventDefault();
    const src = dragSrc.current;
    if (src === null || src === idx) { setDragOverIdx(null); return; }
    const next = [...fields];
    const [item] = next.splice(src, 1);
    next.splice(idx, 0, item);
    setFields(prev => { setUndoStack(u => [...u.slice(-19), prev]); setRedoStack([]); return next; });
    dragSrc.current = null; setDragOverIdx(null);
  };
  const onDragEnd = (e) => { dragSrc.current = null; setDragOverIdx(null); };

  /* ── Save ── */
  const [saved, setSaved] = useState(false);
  const handleSave = () => {
    setSaved(true);
    showToast("Form saved successfully");
    setTimeout(() => setSaved(false), 2500);
  };

  return (
    <div className="fb-page">

      {/* ── Top bar ── */}
      <div className="fb-topbar">
        <div className="fb-topbar-left">
          <input
            className="fb-rp-title-input"
            value={formTitle}
            onChange={e=>setFormTitle(e.target.value)}
            placeholder="Form title…"
          />
        </div>
        <FieldsTopBar onAdd={addField} counts={fieldCounts} />
        <div className="fb-topbar-right">
          <button className="fb-rp-footer-btn fb-rp-preview-btn" onClick={()=>setShowPreview(true)}>
            <Eye size={15}/> Preview
          </button>
          <button className={`fb-rp-footer-btn fb-rp-save-btn${saved?" fb-btn-saved":""}`} onClick={handleSave}>
            <Save size={15}/>{saved ? "Saved!" : "Save Form"}
          </button>
        </div>
      </div>

      <div className="fb-body">

        {/* ── Canvas ── */}
        <main className="fb-canvas" onClick={()=>setSelectedId(null)}>
          <div className="fb-form-card" onClick={e=>e.stopPropagation()}>

            {/* Fields */}
            {fields.length === 0 ? (
              <div className="fb-canvas-empty">
                <div className="fb-empty-icon-wrap"><LayoutTemplate size={28} strokeWidth={1.4}/></div>
                <p className="fb-empty-title">Drop fields here</p>
                <p className="fb-empty-sub">Click any field group in the top bar to add fields</p>
              </div>
            ) : (
              <div className="fb-canvas-list">
                {fields.map((field, idx) => (
                  <div
                    key={field.id}
                    onDragOver={e=>{ e.preventDefault(); e.dataTransfer.dropEffect="move"; setDragOverIdx(idx); }}
                    onDrop={e=>{ e.preventDefault(); onDrop(e,idx); }}
                    className={`fb-drop-zone${dragOverIdx===idx?" drop-active":""}`}
                  >
                    <CanvasCard
                      field={field} index={idx} total={fields.length}
                      isSelected={selectedId===field.id}
                      onSelect={()=>setSelectedId(field.id)}
                      onDelete={()=>deleteField(field.id)}
                      onDragStart={e=>{ e.stopPropagation(); onDragStart(e,idx); }}
                      onDragEnd={onDragEnd}
                      onDuplicate={()=>duplicateField(field.id)}
                    />
                  </div>
                ))}
              </div>
            )}

          </div>
        </main>

        {/* ── Right panel: properties ── */}
        <aside className="fb-props-panel">

          <div className="fb-rp-props-scroll" style={{display: selectedField ? "flex" : "none", flexDirection: "column"}}>
            <PropertiesPanel
              field={selectedField}
              onChange={patch => selectedField && updateField(selectedField.id, patch)}
            />
          </div>

          {/* Submit button config */}
          <div className="fb-submit-pal" style={{marginTop:"auto"}}>
            <div className="fb-submit-pal-hd">
              <span className="fb-group-dot" style={{background:"var(--primaryColor)"}}/>
              <span className="fb-submit-pal-label">Submit Button</span>
            </div>
            <div className="fb-submit-pal-body">
              <input className="fb-pi" value={submitLabel} onChange={e=>setSubmitLabel(e.target.value)} placeholder="Button label…"/>
              <button className="fb-submit-pal-preview">{submitLabel||"Submit"}</button>
            </div>
          </div>

          {/* Bottom actions */}
          <div className="fb-rp-footer">
            {fields.length > 0 && (
              <button className="fb-topbar-clear-btn" onClick={()=>setShowClear(true)}>
                <Trash2 size={13}/> Clear All
              </button>
            )}
          </div>
        </aside>
      </div>

      {/* ── Modals ── */}
      {showPreview && (
        <PreviewModal
          fields={fields} formTitle={formTitle} formDesc={formDesc}
          submitLabel={submitLabel} onClose={()=>setShowPreview(false)}
        />
      )}

      {showClear && (
        <div className="fb-overlay" onClick={()=>setShowClear(false)}>
          <div className="fb-confirm-modal" onClick={e=>e.stopPropagation()}>
            <div className="fb-confirm-icon"><Trash2 size={22} color="#ef4444"/></div>
            <h5 className="fb-confirm-title">Clear all fields?</h5>
            <p className="fb-confirm-sub">This will remove all {fields.length} field{fields.length!==1?"s":""} from the canvas. This can be undone.</p>
            <div className="fb-confirm-btns">
              <button className="fb-btn fb-btn-ghost" onClick={()=>setShowClear(false)}>Cancel</button>
              <button className="fb-btn fb-btn-danger" onClick={clearAll}><Trash2 size={14}/> Clear All</button>
            </div>
          </div>
        </div>
      )}

      {toast && <Toast msg={toast.msg} icon={toast.icon}/>}
    </div>
  );
};

export default FormBuilder;
